/**
 * Shared constants for infection log components.
 * Single source of truth for type labels, site labels, and COVID symptom keys.
 */

import type {
  DiscoveryLocation,
  InfectionSite,
  InfectionType,
} from '../../services/api';

export const INFECTION_TYPE_LABELS: Partial<Record<InfectionType, string>> = {
  bronchiolitis: 'Bronchiolitis',
  bronchitis: 'Bronchitis',
  cellulitis: 'Cellulitis',
  chickenpox: 'Chickenpox',
  chlamydia: 'Chlamydia',
  conjunctivitis: 'Conjunctivitis',
  covid_19: 'COVID-19',
  gonorrhea: 'Gonorrhea',
  influenza: 'Influenza',
  lyme_disease: 'Lyme Disease',
  meningitis: 'Meningitis',
  mrsa: 'MRSA',
  otitis_externa: 'Otitis (Externa)',
  otitis_media: 'Otitis (Media)',
  pharyngitis: 'Pharyngitis',
  pneumonia: 'Pneumonia',
  prostatitis: 'Prostatitis',
  pyelonephritis: 'Pyelonephritis',
  shingles: 'Shingles',
  sinusitis: 'Sinusitis',
  soft_tissue: 'Soft Tissue',
  staph: 'Staph',
  strep: 'Strep',
  tetanus: 'Tetanus',
  tuberculosis: 'Tuberculosis',
  ulcer: 'Ulcer',
  urinary: 'Urinary',
  vrsa: 'VRSA',
  other: 'Other',
};

/** Array form for use in Select dropdowns. */
export const INFECTION_TYPE_OPTIONS = (
  Object.entries(INFECTION_TYPE_LABELS) as [InfectionType, string][]
).map(([value, label]) => ({ value, label }));

export const COVID_SYMPTOMS: { key: string; label: string }[] = [
  { key: 'cough', label: 'Cough' },
  { key: 'congestion', label: 'Congestion or Runny Nose' },
  { key: 'shortness_of_breath', label: 'Shortness of Breath' },
  { key: 'headache', label: 'Headache' },
  { key: 'fever_chills', label: 'Fever or Chills' },
  { key: 'loss_taste_smell', label: 'New Loss of Taste or Smell' },
  { key: 'sore_throat', label: 'Sore Throat' },
  { key: 'fatigue', label: 'Fatigue' },
  { key: 'diarrhea', label: 'Diarrhea' },
  { key: 'muscle_aches', label: 'Muscle or Body Aches' },
  { key: 'nausea_vomiting', label: 'Nausea or Vomiting' },
  { key: 'new_confusion', label: 'New Confusion' },
];

/** Short labels used in the detail dialog's screening history display. */
export const COVID_SYMPTOM_SHORT_LABELS: Record<string, string> = {
  cough: 'Cough',
  congestion: 'Congestion/Runny Nose',
  shortness_of_breath: 'Shortness of Breath',
  headache: 'Headache',
  fever_chills: 'Fever or Chills',
  loss_taste_smell: 'New Loss of Taste or Smell',
  sore_throat: 'Sore Throat',
  fatigue: 'Fatigue',
  diarrhea: 'Diarrhea',
  muscle_aches: 'Muscle or Body Aches',
  nausea_vomiting: 'Nausea or Vomiting',
  new_confusion: 'New Confusion',
};

export const INFECTION_SITES: { value: InfectionSite; label: string }[] = [
  { value: 'abdominal', label: 'Abdominal' },
  { value: 'catheter', label: 'Catheter' },
  { value: 'cervix', label: 'Cervix' },
  { value: 'cns', label: 'CNS' },
  { value: 'ear', label: 'Ear' },
  { value: 'eye', label: 'Eye' },
  { value: 'foot', label: 'Foot' },
  { value: 'iv_site', label: 'IV Site' },
  { value: 'mouth', label: 'Mouth' },
  { value: 'urinary_tract', label: 'Urinary Tract' },
  { value: 'respiratory', label: 'Respiratory' },
  { value: 'surgical_wound', label: 'Surgical Wound' },
  { value: 'throat', label: 'Throat' },
  { value: 'other', label: 'Other' },
];

export const DISCOVERY_LOCATIONS: {
  value: DiscoveryLocation;
  label: string;
}[] = [
  { value: 'alf', label: 'ALF' },
  { value: 'hospital', label: 'Hospital' },
  { value: 'patient_home', label: 'Patient Home' },
  { value: 'psychiatric_facility', label: 'Psychiatric Facility' },
  { value: 'snf', label: 'SNF' },
  { value: 'other', label: 'Other' },
];
